/**
 * Trade Now. Two people at a table, one live session on the server.
 *
 * Every call the app makes for an in-person trade goes through here, so the
 * server contract (~/Apps/CardChaseCorporate/TradeNow-contract.md) is wired in
 * exactly one place. Every RPC returns the whole session, which is the only
 * state the screens hold; the realtime subscription just asks for it again.
 */

import { supabase } from '../lib/supabase';

export const CONDITIONS = ['NM', 'LP', 'MP', 'HP', 'DMG'];
export const CONDITION_LABEL = {
  NM: 'Near Mint', LP: 'Lightly Played', MP: 'Moderately Played', HP: 'Heavily Played', DMG: 'Damaged',
};

/** The link a QR carries. Opening it in the app joins the trade. */
export const joinLink = (token) => `https://app.cardchase.org/tn/${token}`;

/**
 * Where the join screen parks its route while the profile picker verifies a
 * profile switch, so the picker can send the person back to the trade.
 * sessionStorage, because it is only meaningful for this one detour.
 */
export const JOIN_RETURN_KEY = 'tradeJoinReturn';

/**
 * The join RPC speaks in short sentences meant for a developer. Two of them
 * belong in a person's own words at a table - above all the one a code
 * photographed yesterday produces, which is "no open trade matches that code".
 */
export function friendlyJoinError(message) {
  const m = String(message || '');
  if (/no open trade matches/i.test(m)) {
    return 'That code is no longer live. Trades close when they finish or when the other phone leaves, so ask them to open Trade Now again and show you a fresh code.';
  }
  if (/already joined/i.test(m)) return 'Someone else already joined that trade.';
  if (/your own trade/i.test(m)) return 'That is your own code. The other person scans yours, not the other way round.';
  if (/already have a trade open/i.test(m)) return 'You already have a trade open. Finish or cancel that one first.';
  return m || 'That trade could not be joined.';
}

/** A scanned QR or a typed code, normalised to what the server accepts. */
export function codeFromScan(text) {
  const t = String(text || '').trim();
  const m = t.match(/\/tn\/([A-Za-z0-9_-]{6,})/) || t.match(/^cardchase:\/\/trade\/([A-Za-z0-9_-]{6,})$/);
  if (m) return m[1];
  const digits = t.replace(/\D/g, '');
  if (/^\d{4}$/.test(t)) return t;
  if (/^[A-Za-z0-9_-]{16}$/.test(t)) return t;
  return digits.length === 4 ? digits : t;
}

async function rpc(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw new Error(error.message || `${name} failed`);
  return data;
}

const asArg = (as) => (as ? { p_as: as } : {});

export const isAllowed = (as) => rpc('trade_now_allowed', asArg(as)).then(Boolean).catch(() => false);
export const current = (as) => rpc('trade_now_current', asArg(as));
export const create = (as) => rpc('trade_now_create', asArg(as));
export const join = (codeOrToken, as) => rpc('trade_now_join', { p_code_or_token: codeOrToken, ...asArg(as) });
/**
 * Who is behind a code, without joining. Read-only, no side effects, so the
 * "start a trade with them?" step can show who it is and why a code will not
 * work before anyone commits. Resolves to { ok, session_id, host_user_id } or
 * { ok: false, reason }.
 */
export const peek = (codeOrToken, as) => rpc('trade_now_peek', { p_code_or_token: codeOrToken, ...asArg(as) });
export const addCard = (session, card, as) => rpc('trade_now_add', { p_session: session, p_card: card, ...asArg(as) });
export const removeCard = (session, card, as) => rpc('trade_now_remove', { p_session: session, p_card: card, ...asArg(as) });
export const setCondition = (session, card, condition, as) =>
  rpc('trade_now_set_condition', { p_session: session, p_card: card, p_condition: condition || null, ...asArg(as) });
export const confirm = (session, as) => rpc('trade_now_confirm', { p_session: session, ...asArg(as) });
export const cancel = (session, as) => rpc('trade_now_cancel', { p_session: session, ...asArg(as) });
/**
 * A note and any photos somebody wants to keep with a trade.
 *
 * `photoUrls` is a list. It used to be a single url, and the server keeps both
 * columns filled - the first of the list also lands in the old `photo_url` - so
 * a phone still on the previous bundle reads its own photo rather than a blank.
 */
export const saveMemory = (trade, note, photoUrls, as) =>
  rpc('trade_memory_save', {
    p_trade: trade,
    p_note: note || null,
    p_photo_urls: Array.isArray(photoUrls) ? photoUrls.filter(Boolean) : [photoUrls].filter(Boolean),
    ...asArg(as),
  });
export const journey = (card) => rpc('card_journey', { p_card: card }).then((rows) => rows || []);
export const getControls = (child) => rpc('trade_now_get_controls', { p_child: child });
export const setControls = (child, { allowTradeNow, allowConditionChange, threshold }) =>
  rpc('trade_now_set_controls', {
    p_child: child,
    p_allow_trade_now: allowTradeNow,
    p_allow_condition_change: allowConditionChange,
    p_trade_value_threshold: threshold == null || threshold === '' ? null : Number(threshold),
  });

/** Add several cards one after another; the last session wins. Errors carry the card. */
export async function addMany(session, cardIds, as) {
  let last = null;
  const failed = [];
  for (const id of cardIds) {
    try { last = await addCard(session, id, as); } catch (err) { failed.push({ id, message: err.message }); }
  }
  return { session: last, failed };
}

/**
 * Follow a session. Any change on either table re-reads it. Returns the
 * unsubscribe. `onSession` receives the fresh session or null when it is gone.
 */
export function subscribe(sessionId, as, onSession) {
  let alive = true;
  // Once a session completes or is cancelled it is no longer "current", so the
  // re-read comes back null. The raw row from the event says which, and the
  // screen needs that to tell "trade done" from "they left".
  const refetch = async (payload) => {
    try {
      const s = await current(as);
      if (alive) onSession(s && s.id === sessionId ? s : null, payload?.new || null);
    } catch (err) {
      console.warn('[tradeNow] refetch', err.message);
    }
  };
  const channel = supabase.channel(`trade:${sessionId}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'trade_sessions', filter: `id=eq.${sessionId}` }, refetch)
    .on('postgres_changes', { event: '*', schema: 'public', table: 'trade_session_items', filter: `session_id=eq.${sessionId}` }, () => refetch(null))
    .subscribe();
  return () => { alive = false; supabase.removeChannel(channel); };
}

/** Which side of the table is mine, given who I am. */
export function mySide(session, meId) {
  if (!session) return null;
  if (session.host_user_id === meId) return 'host';
  if (session.guest_user_id === meId) return 'guest';
  return null;
}

// Null-safe: screens ask before the session has loaded, and a null here took
// the whole picker down to a black screen.
export const otherPerson = (session, meId) => {
  if (!session) return null;
  return mySide(session, meId) === 'host' ? session.guest : session.host;
};

export function itemsFor(session, side) {
  const key = side === 'host' ? 'host_gives' : 'guest_gives';
  return (session?.items || []).filter((i) => i.side === key);
}

export const totalFor = (session, side) => Number(side === 'host' ? session?.host_total : session?.guest_total) || 0;

export const money = (n) => {
  const v = Number(n) || 0;
  return v >= 1000 ? `$${Math.round(v).toLocaleString()}` : `$${v.toFixed(2)}`;
};

/**
 * The one line under the totals. Plain words, no verdict: who is giving more
 * and by how much, or that it is even. `theirName` is the other person.
 */
export function differenceLine(session, meId, theirName = 'They') {
  const side = mySide(session, meId);
  if (!side) return '';
  const mine = totalFor(session, side);
  const theirs = totalFor(session, side === 'host' ? 'guest' : 'host');
  if (mine === 0 && theirs === 0) return 'Nothing on the table yet';
  const gap = Math.abs(mine - theirs);
  if (gap < 0.005) return 'Even trade';
  return mine > theirs
    ? `You are giving ${money(gap)} more than you are getting`
    : `${theirName} is giving ${money(gap)} more than you are`;
}

export const iConfirmed = (session, meId) => {
  const side = mySide(session, meId);
  return side === 'host' ? Boolean(session?.host_confirmed_at) : side === 'guest' ? Boolean(session?.guest_confirmed_at) : false;
};
export const theyConfirmed = (session, meId) => {
  const side = mySide(session, meId);
  return side === 'host' ? Boolean(session?.guest_confirmed_at) : side === 'guest' ? Boolean(session?.host_confirmed_at) : false;
};

export const isLive = (session) => Boolean(session) && (session.status === 'open' || session.status === 'confirmed');

const api = {
  isAllowed, current, create, join, peek, addCard, removeCard, setCondition, confirm, cancel, saveMemory, journey,
  getControls, setControls, addMany, subscribe, mySide, otherPerson, itemsFor, totalFor, money, differenceLine,
  iConfirmed, theyConfirmed, isLive, joinLink, codeFromScan, friendlyJoinError, CONDITIONS, CONDITION_LABEL,
};
export default api;
